"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AddStudentsToCohortForm = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * AddStudentsToCohortForm - UUID textarea form for adding students to a cohort
 *
 * Matches Angular's cohort-plan-detail.component.ts add students functionality:
 * - Textarea for student IDs (one UUID per line)
 * - Client-side validation: UUID regex, duplicate check
 * - Confirmation modal before API call
 * - Server error categorization (wrong tenant, existing plan, no student program)
 * - Informational error modal so user can fix IDs before retrying
 */
const react_1 = require("react");
const degreePlanApi_1 = require("../helpers/degreePlanApi");
const Button_1 = require("./Button");
const Modal_1 = require("./Modal");
const ValidationExceptionsModal_1 = require("./ValidationExceptionsModal");
const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const AddStudentsToCohortForm = ({ cohortPlan, onStudentsAdded, }) => {
    const [studentIdsText, setStudentIdsText] = (0, react_1.useState)('');
    const [validationError, setValidationError] = (0, react_1.useState)('');
    const [showConfirmModal, setShowConfirmModal] = (0, react_1.useState)(false);
    const [showErrorModal, setShowErrorModal] = (0, react_1.useState)(false);
    const [serverErrors, setServerErrors] = (0, react_1.useState)(null);
    const [isSubmitting, setIsSubmitting] = (0, react_1.useState)(false);
    const [successMessage, setSuccessMessage] = (0, react_1.useState)('');
    const [validatedIds, setValidatedIds] = (0, react_1.useState)([]);
    const parseStudentIds = (0, react_1.useCallback)(() => {
        return studentIdsText
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
    }, [studentIdsText]);
    const validate = (0, react_1.useCallback)(() => {
        setValidationError('');
        const ids = parseStudentIds();
        if (ids.length === 0) {
            setValidationError('Please enter at least one student ID');
            return false;
        }
        // Validate UUID format
        const invalidIds = ids.filter((id) => !UUID_PATTERN.test(id));
        if (invalidIds.length > 0) {
            setValidationError(`Invalid UUID format: ${invalidIds.slice(0, 3).join(', ')}${invalidIds.length > 3 ? ` and ${invalidIds.length - 3} more` : ''}`);
            return false;
        }
        // Check for duplicates
        const seen = new Set();
        const duplicates = [];
        for (const id of ids) {
            if (seen.has(id.toLowerCase())) {
                duplicates.push(id);
            }
            seen.add(id.toLowerCase());
        }
        if (duplicates.length > 0) {
            setValidationError(`Duplicate IDs found: ${duplicates.slice(0, 3).join(', ')}`);
            return false;
        }
        return true;
    }, [parseStudentIds]);
    const handleAdd = (0, react_1.useCallback)(() => {
        if (!validate())
            return;
        const ids = parseStudentIds();
        setValidatedIds(ids);
        setShowConfirmModal(true);
    }, [validate, parseStudentIds]);
    const handleConfirmAdd = (0, react_1.useCallback)((ids) => __awaiter(void 0, void 0, void 0, function* () {
        const studentIds = ids || validatedIds;
        setShowConfirmModal(false);
        setIsSubmitting(true);
        setServerErrors(null);
        try {
            yield degreePlanApi_1.degreePlanApi.createAdditionalStudentPlansInCohort(cohortPlan, studentIds);
            setSuccessMessage('Student(s) added!');
            setStudentIdsText('');
            setTimeout(() => setSuccessMessage(''), 3000);
            onStudentsAdded();
        }
        catch (err) {
            if ((err === null || err === void 0 ? void 0 : err.status) === 400 && (err === null || err === void 0 ? void 0 : err.details)) {
                const normalized = (0, ValidationExceptionsModal_1.normalizeServerErrors)(err.details);
                if ((0, ValidationExceptionsModal_1.hasValidationErrors)(normalized)) {
                    setServerErrors(normalized);
                    setShowErrorModal(true);
                    return;
                }
            }
            setValidationError(err instanceof Error
                ? err.message
                : ((err === null || err === void 0 ? void 0 : err.message) || 'Failed to add students'));
        }
        finally {
            setIsSubmitting(false);
        }
    }), [validatedIds, cohortPlan, onStudentsAdded]);
    return ((0, jsx_runtime_1.jsxs)("div", { className: "add-students-form", children: [(0, jsx_runtime_1.jsx)("h3", { children: "Add Additional Students to Cohort" }), successMessage && ((0, jsx_runtime_1.jsx)("div", { role: "status", style: { color: 'green', marginBottom: '1rem' }, children: successMessage })), validationError && ((0, jsx_runtime_1.jsx)("div", { role: "alert", style: { color: 'red', marginBottom: '1rem' }, children: validationError })), (0, jsx_runtime_1.jsxs)("div", { style: { marginBottom: '1rem' }, children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "student-ids-textarea", children: "Student IDs (one UUID per line):" }), (0, jsx_runtime_1.jsx)("textarea", { id: "student-ids-textarea", value: studentIdsText, onChange: (e) => setStudentIdsText(e.target.value), rows: 6, style: { width: '100%', fontFamily: 'monospace', marginTop: '0.5rem' }, placeholder: "Enter student UUIDs, one per line...", disabled: isSubmitting }), (0, jsx_runtime_1.jsxs)("p", { style: { fontSize: '0.875rem', color: '#666' }, children: [parseStudentIds().length, " student ID(s) entered"] })] }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleAdd, disabled: isSubmitting || studentIdsText.trim().length === 0, loading: isSubmitting, children: "Add Students" }), (0, jsx_runtime_1.jsx)(Modal_1.Modal, { isOpen: showConfirmModal, onClose: () => setShowConfirmModal(false), title: "Confirm Add Students", footer: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "secondary", onClick: () => setShowConfirmModal(false), children: "Cancel" }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => handleConfirmAdd(), children: "Confirm" })] }), children: (0, jsx_runtime_1.jsxs)("p", { children: ["Are you sure you want to add ", validatedIds.length, " student(s) to this cohort?"] }) }), (0, jsx_runtime_1.jsx)(ValidationExceptionsModal_1.ValidationExceptionsModal, { isOpen: showErrorModal, onClose: () => setShowErrorModal(false), serverErrors: serverErrors })] }));
};
exports.AddStudentsToCohortForm = AddStudentsToCohortForm;
exports.default = exports.AddStudentsToCohortForm;
